import {
  Box,
  Button,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  Switch,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import React, { useCallback, useEffect, useState } from "react";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Typography from "legacy-components/typography/Typography";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { useDialogProps, Dialog } from "legacy-components/dialog/Dialog";

import ApplicationService from "pages/home/applications/components/v2.0/ApplicationService";
import { formatPublisherHttpError } from "../services/ApiService";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { useAuthorization } from "../../authorizer/components/AuthorizationProvider";
import { TimeUnit } from "./ThrottlingDetails";

const supportRegionalDeployments =
  process.env.REACT_APP_SUPPORT_REGIONAL_DEPLOYMENTS === "true";

interface SubscriptionQuotaForm {
  onSuccess?: () => void;
  deploymentId: string;
  availableTimeUnit:readonly TimeUnit[];
  subscriptionQuotaEabled: boolean;
  SubscriptionQuotaConfig: {
    enabled: boolean;
    limit: number;
    interval: number;
    unit: string;
  } | null;
  spikeArrestConfig: {
    enabled: boolean;
    limit: number;
    unit: string;
  } | null;
  deploymentStatus: string | null;
  serviceId: string;
}
const SubscriptionQuota: React.FC<SubscriptionQuotaForm> = ({
  onSuccess,
  deploymentId,
  availableTimeUnit,
  subscriptionQuotaEabled,
  SubscriptionQuotaConfig,
  deploymentStatus,
  spikeArrestConfig,
    serviceId
}) => {
  const [subscriptionQuotaLimit, setsubscriptionQuotaLimit] =
    useState<number| string>(SubscriptionQuotaConfig?.limit ?? "");
  const [interval, setInterval] = useState<number | string>(SubscriptionQuotaConfig?.interval ?? "");
  const [timeMetrics, setTimeMetrics] = useState<string>(SubscriptionQuotaConfig?.unit  ?? availableTimeUnit[0].value);
  const [isSubscriptionQuotaActive, setIsSubscriptionQuotaActive] =
    useState<boolean>(subscriptionQuotaEabled=== undefined ? true : subscriptionQuotaEabled);
const [hasFormError, setHasFormError] = useState<boolean>(false);
const [hasIntervalForm, setHasIntervalForm] = useState<boolean>(false);

  const { open, toggle } = useDialogProps();

  const resetForm = useCallback(() => {
    setsubscriptionQuotaLimit(SubscriptionQuotaConfig?.limit ?? "");
    setInterval(SubscriptionQuotaConfig?.interval ?? "");
    setTimeMetrics(SubscriptionQuotaConfig?.unit ?? availableTimeUnit[0].value);
  }, [SubscriptionQuotaConfig, availableTimeUnit]);

  const handleClose = useCallback(() => {
    resetForm();
    toggle();
  }, [resetForm, toggle]);

  var requestBody = {
    spikeArrestEnabled: spikeArrestConfig?.enabled ?? false,
    spikeConfig: {
      limit: spikeArrestConfig?.limit ?? 300,
      unit: spikeArrestConfig?.unit ?? "SECOND",
    },
    quotaEnabled: isSubscriptionQuotaActive,
    quotaConfig: [
      {
        path: "/*",
        verb: "*",
        limit: subscriptionQuotaLimit === '' ? 0 : Number(subscriptionQuotaLimit),
        interval: interval === '' ? 0 : Number(interval),
        unit: timeMetrics,
      },
    ],
  };

  const onSpikeArrestToggle = useCallback(() => {
    if(!toggle || !SubscriptionQuotaConfig || !setIsSubscriptionQuotaActive){
      return ;
    }
    else{
      setIsSubscriptionQuotaActive(!isSubscriptionQuotaActive);

    }
  }, [setIsSubscriptionQuotaActive, isSubscriptionQuotaActive, SubscriptionQuotaConfig, toggle]);

  const { state, subscribe } = useAsyncAction(() =>
    supportRegionalDeployments
      ? ApplicationService.createApiThrottlingV2(deploymentId, requestBody)
      : ApplicationService.createApiThrottling(deploymentId, requestBody)
  );


  const handleMinInterval = ()=>{
    if (timeMetrics === "MINUTE") {
      return 5;
    } else {
      return 1;
    }
  }

  const handleMaxLimit = () => {
    if (timeMetrics === "MONTH") {
      return 2000000000;
    }
    else {
      return 20000000;
    }
  }

  const handleSubmit = useCallback(() => {
     if(!subscribe || !toggle || !setTimeMetrics){
      return;
    }
    subscribe();
    if (open) {
      resetForm();
      setTimeMetrics(SubscriptionQuotaConfig?.unit ?? availableTimeUnit[0].value);
      toggle();
    }
  }, [subscribe, toggle, open, resetForm, setTimeMetrics, SubscriptionQuotaConfig, availableTimeUnit]);

  useEffect(() => {
    if (!state?.loaded || state?.error || !onSuccess) {
      return;
    }
    onSuccess();
  }, [state?.loaded, state?.error, onSuccess]);


  useEffect(() => {
    if (!subscribe || !toggle || subscriptionQuotaEabled === undefined) {
      return;
    }

    if(SubscriptionQuotaConfig === null){
      setIsSubscriptionQuotaActive(open);
    }

    if (subscriptionQuotaEabled === isSubscriptionQuotaActive) {
      return;
    }
    if (isSubscriptionQuotaActive === false) {
      subscribe();
    }

    if(SubscriptionQuotaConfig !== null && isSubscriptionQuotaActive === true) {
      setIsSubscriptionQuotaActive(!isSubscriptionQuotaActive);
      subscribe()
    }
  }, [isSubscriptionQuotaActive, subscribe, toggle, subscriptionQuotaEabled, setIsSubscriptionQuotaActive, SubscriptionQuotaConfig, open]);
  const { isActionAllowed } = useAuthorization("SERVICE" , serviceId);
  const canUpdate = isActionAllowed("update");
  const isSwitchDisabled = deploymentStatus === "DEPLOY_INITIATED" || SubscriptionQuotaConfig === undefined || SubscriptionQuotaConfig === null || state?.loading || !canUpdate;
  const isUpdateButtonDisabled = deploymentStatus === "DEPLOY_INITIATED" || subscriptionQuotaLimit === "" || timeMetrics === "" || interval === "" || state?.loading || hasFormError || hasIntervalForm;

  const throttlingErrorMessage =
    state?.error != null
      ? formatPublisherHttpError(
          state.error,
          "Could not update throttling. Please try again."
        )
      : null;

  return (
    <div>
      {throttlingErrorMessage && (
        <Alert severity="error" style={{ marginBottom: 8 }}>
          {throttlingErrorMessage}
        </Alert>
      )}
      <Switch
        size="small"
        color="primary"
        onChange={onSpikeArrestToggle}
        checked={isSubscriptionQuotaActive}
        disabled={isSwitchDisabled}
      />
      <IconButton onClick={toggle} disabled={deploymentStatus === "DEPLOY_INITIATED" || !canUpdate}>
        <EditOutlinedIcon />
      </IconButton>
      <Dialog
        open={open}
        title="Subscription Quota"
        onClose={handleClose}>
        <Box>
          <Typography type="body">
            API Transaction limit
            <span className="required">*</span>
          </Typography>
          <InputContainer
            variant="standard"
            autoFocus
            margin="dense"
            id="subscriptionQuotaLimit"
            placeholder="Number of API hits allowed"
            fullWidth
            required={true}
            rows={1}
            value={subscriptionQuotaLimit === "" ? "" : subscriptionQuotaLimit.toString()}
            style={{ width: "100%" }}
            onChange={(event) => {
                setsubscriptionQuotaLimit(event.target.value);
            }}
            setError={setHasFormError}
            isOnTypeValidationEnabled={true}
            validate={{
              required: false,
              pattern: "^\\d*$",
              min: 1,
              max: handleMaxLimit(),
            }}

          />
          <Box
            display={"flex"}
            flexDirection="row"
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography type="body">
              Time
              <span className="required">*</span>
            </Typography>

            <Typography>
              Time metrics
              <span className="required">*</span>
            </Typography>
          </Box>
          <Box
            display={"flex"}
            flexDirection="row"
            style={{ width: "100%" }}
            justifyContent="space-around"
          >
            <InputContainer
              variant="standard"
              autoFocus
              fullWidth
              rows={1}
              margin="dense"
              id="interval"
              placeholder="Time in numbers"
              style={{ width: "80%" }}
              required={true}
              isOnTypeValidationEnabled={true}
              setError={setHasIntervalForm}
              value={interval === "" ? "" : interval.toString()}
              onChange={(event) => {
                  setInterval(event.target.value);
              }}
              validate={{
                pattern: "^\\d*$",
                min: handleMinInterval(),
                max: 500,
              }}
            />
            <Select
              id="subscriptionQuota"
              IconComponent={ExpandMoreIcon}
              variant="outlined"
              style={{ width: "48%", height: "56px", marginTop: "8px" }}
              className="header-selector resource-version-select"
              placeholder="Time Metrics"
              value={timeMetrics}
              onChange={(event) => {
                const value = event.target.value as string;
                setTimeMetrics(value);
              }}
            >
              {availableTimeUnit?.map((unit) => {
                return (
                  <MenuItem value={unit.value} key={unit.value}>
                    {unit.key}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
          <DialogActions style={{ marginTop: "20px" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isUpdateButtonDisabled}
            >
              Update
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default SubscriptionQuota;